/**
 * useNotificationPreferences
 *
 * Fetches and mutates the current user's notification preferences via Central
 * using canonical paths from @levelup/central-notifications.
 *
 * - GET    `${CENTRAL_URL}/api/notifications/preferences`        → list
 * - PATCH  `${CENTRAL_URL}/api/notifications/preferences/:cat`   → toggle channel
 *
 * Bearer-only. No cookies. No proxy.
 *
 * Mutations are OPTIMISTIC: the local CategoryPreference is updated
 * immediately, then reverted if the PATCH fails. Empty preferences
 * are a real "not-configured" state, not an error.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  NOTIFICATION_PREFERENCES_PATH,
  type CategoryPreference,
  type NotificationChannel,
  type PreferencesResponse,
  type UpdateCategoryParams,
  type UpdateCategoryResponse,
} from '@levelup/central-notifications';
import { selectCentralAccessToken } from '../store/slices/centralAuthSlice';
import { CENTRAL_URL } from '../config/centralAuth';
import type { NotificationsFetchState } from '../adapters/settingsAdapter';
import { reportCentralFailure, terminalForCaller } from '../auth/reportCentralFailure';

/**
 * Thrown when `reportCentralFailure` has already raised the right surface, so
 * this hook must NOT produce an error state of its own. A sentinel rather than a
 * flag because the decision is made inside a `.then` and has to skip the
 * `.catch` that follows it.
 */
class CentralVerdictHandled extends Error {}

interface UseNotificationPreferencesReturn {
  state: NotificationsFetchState;
  /** Toggle a channel for a category. Optimistic; reverts on PATCH failure. */
  updateCategory: (params: UpdateCategoryParams) => Promise<void>;
}

function applyChannelToggle(
  prefs: CategoryPreference[],
  category: string,
  channels: Partial<Record<NotificationChannel, boolean>>,
): CategoryPreference[] {
  return prefs.map((p) => {
    if (p.category !== category) return p;
    const nextChannels = { ...p.channels };
    for (const [ch, enabled] of Object.entries(channels) as [
      NotificationChannel,
      boolean,
    ][]) {
      const existing = nextChannels[ch];
      if (existing) nextChannels[ch] = { ...existing, enabled };
    }
    return { ...p, channels: nextChannels };
  });
}

export function useNotificationPreferences(): UseNotificationPreferencesReturn {
  const token = useSelector(selectCentralAccessToken);
  const dispatch = useDispatch();
  const [state, setState] = useState<NotificationsFetchState>({ status: 'idle' });

  // Mirror of the latest "ready" preferences for optimistic updates / revert
  const lastReadyRef = useRef<CategoryPreference[] | null>(null);

  useEffect(() => {
    if (!token || !CENTRAL_URL) {
      setState({ status: 'error', message: 'Not authenticated' });
      return;
    }

    let cancelled = false;
    setState({ status: 'loading' });

    // A raw fetch straight to Central, so NOTHING else classifies this answer.
    // It used to set `status: 'error', message: 'Failed to load preferences: 401'`
    // for a 401 — a generic product error standing in for a terminal auth state,
    // with no login transition at all, which is exactly the failure this
    // precedence work exists to remove. BeHeard runs no refresh proxy, so after
    // the fifteen-minute access-token lifetime this 401 is the NORMAL answer here,
    // not an edge case.
    //
    // Now the shared contract decides, and when the verdict has its own surface
    // (Central login, workspace-closed, access-denied) this hook renders NOTHING
    // rather than painting over it. A genuine authenticated failure — a 500, an
    // outage, an unreadable answer — still produces the error state below, which
    // is what that state is for.
    fetch(`${CENTRAL_URL}${NOTIFICATION_PREFERENCES_PATH}`, {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'application/json',
      },
    })
      .then(async (res) => {
        if (!res.ok) {
          const body = await res.json().catch(() => undefined);
          const action = reportCentralFailure(dispatch, {
            status: res.status,
            body,
            source: 'useNotificationPreferences',
          });
          if (terminalForCaller(action)) {
            // The verdict owns the screen. Stay in 'loading' so this hook's
            // consumer renders a spinner, never an error, while the gate above
            // it carries out the transition.
            throw new CentralVerdictHandled();
          }
          throw new Error(`Failed to load preferences: ${res.status}`);
        }
        return (await res.json()) as PreferencesResponse;
      })
      .then((body) => {
        if (cancelled) return;
        const prefs = body.preferences ?? [];
        lastReadyRef.current = prefs;
        if (prefs.length === 0) {
          setState({ status: 'not-configured' });
        } else {
          setState({ status: 'ready', preferences: prefs });
        }
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof CentralVerdictHandled) return;
        setState({
          status: 'error',
          message: err instanceof Error ? err.message : 'Failed to load preferences',
        });
      });

    return () => {
      cancelled = true;
    };
  }, [token, dispatch]);

  const updateCategory = useCallback(
    async (params: UpdateCategoryParams) => {
      if (!token || !CENTRAL_URL) return;
      const prev = lastReadyRef.current;
      if (!prev) return;

      // Optimistic
      const optimistic = applyChannelToggle(prev, params.category, params.channels);
      lastReadyRef.current = optimistic;
      setState({ status: 'ready', preferences: optimistic });

      try {
        const res = await fetch(
          `${CENTRAL_URL}${NOTIFICATION_PREFERENCES_PATH}/${encodeURIComponent(params.category)}`,
          {
            method: 'PATCH',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
            body: JSON.stringify({ channels: params.channels }),
          },
        );
        if (!res.ok) throw new Error(`Update failed: ${res.status}`);
        const body = (await res.json()) as UpdateCategoryResponse;

        // Reconcile with server response (canonical truth for this category)
        const reconciled = (lastReadyRef.current ?? optimistic).map((p) =>
          p.category === body.preference.category ? body.preference : p,
        );
        lastReadyRef.current = reconciled;
        setState({ status: 'ready', preferences: reconciled });
      } catch (err) {
        // Revert
        lastReadyRef.current = prev;
        setState({ status: 'ready', preferences: prev });
        // Surface the failure as a transient message via state.error path is
        // not modeled in NotificationsFetchState 'ready' — log + rely on the
        // toggle visibly snapping back to the prior position.
        // eslint-disable-next-line no-console
        console.error(
          '[NotificationPreferences] PATCH failed:',
          err instanceof Error ? err.message : err,
        );
      }
    },
    [token],
  );

  return { state, updateCategory };
}
